import { Select } from "@msys/ui";
import React from "react";
import { QuantityUnit } from "../../../../clients/graphqlTypes.js";
import { useQuantityUnits } from "../../doc-items/useQuantityUnits.js";

interface Props {
  initialUnit: QuantityUnit;
  // Called once on commit (select close) with the new value.
  onCommit: (quantityUnit: QuantityUnit) => Promise<void> | void;
  // Exits the surrounding InlineEditableCell's edit mode.
  handleBlur: () => void;
}

export function QuantityUnitInlineEditor({
  initialUnit,
  onCommit,
  handleBlur,
}: Props) {
  const [value, setValue] = React.useState<QuantityUnit>(initialUnit);
  const valueRef = React.useRef(value);

  const { quantityUnitOptions } = useQuantityUnits();
  const sealedRef = React.useRef(false);

  const commit = async () => {
    if (sealedRef.current) return;
    sealedRef.current = true;
    // Skip the mutation + refetch when the user closes the Select without
    // changing the unit (including the Escape / outside-click dismiss path) —
    // MUI fires onClose unconditionally on close.
    try {
      if (valueRef.current !== initialUnit) {
        await onCommit(valueRef.current);
      }
    } catch (error) {
      // Commit failed — unseal so the next close retries instead of
      // dead-ending the cell in edit mode. Swallowing here also prevents an
      // unhandled rejection (onClose fires-and-forgets via `void commit()`).
      sealedRef.current = false;
      console.error("quantity-unit commit failed", error);
      return;
    }
    handleBlur();
  };

  return (
    <Select<QuantityUnit>
      size="extra-small"
      variant="outlined"
      options={quantityUnitOptions}
      value={value}
      onChange={newValue => {
        // Update the ref synchronously so commit() (called from onClose in the
        // same tick) sees the new value without waiting for the next render.
        valueRef.current = newValue;
        setValue(newValue);
      }}
      defaultOpen
      fullWidth
      onClose={() => void commit()}
    />
  );
}
